import React, { useCallback, useEffect, useMemo, useState } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Stack from "react-bootstrap/Stack";
import { useNavigate } from "react-router-dom";

import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import {
  getWhatsAppStatus,
  listConversations,
  listWhatsAppTemplates,
} from "../../whatsappApi";
import usePracticeOptions from "./usePracticeOptions";
import InboxModal from "./InboxModal";
import { ThreadRow } from "./ThreadList";
import useWhatsAppSocket from "./useWhatsAppSocket";
import "./whatsapp.css";

/**
 * InboxSection — the dashboard's WhatsApp card.
 *
 * Shows the most recent threads, unread first; the message pane opens in
 * InboxModal (see the comment there for why it is not inline).
 *
 * Rendered as a SectionCard matching home.jsx:26-41 — that component is local
 * to home.jsx, so the shape is reproduced here rather than exported, keeping
 * this card droppable into the dashboard without touching the existing
 * tasks-feature branching.
 */

const PREVIEW_COUNT = 6;

export default function InboxSection() {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { options: practices, practiceNumbers, labelFor } = usePracticeOptions();

  const [practiceNumber, setPracticeNumber] = useState(practiceNumbers[0] || "");
  const [connected, setConnected] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInbox, setShowInbox] = useState(false);
  const [openConversationId, setOpenConversationId] = useState(null);

  const loadConversations = useCallback(async () => {
    if (!practiceNumber) return;
    try {
      const data = await listConversations(practiceNumber, { limit: 50 });
      setConversations(data.conversations || []);
    } catch (err) {
      // A failed refresh should not blank a working card.
      console.error("WhatsApp conversations failed to load", err);
    }
  }, [practiceNumber]);

  const load = useCallback(async () => {
    if (!practiceNumber) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const status = await getWhatsAppStatus(practiceNumber);
      setConnected(!!status?.connected);

      if (status?.connected) {
        const [{ templates: list }] = await Promise.all([
          listWhatsAppTemplates(practiceNumber),
          loadConversations(),
        ]);
        setTemplates(list || []);
      }
    } catch (err) {
      console.error("WhatsApp status failed to load", err);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [practiceNumber, loadConversations]);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates. The practice:<n> rooms already exist server-side, so a new
  // message lands here without a refresh.
  useWhatsAppSocket({
    enabled: !!connected && !!practiceNumber,
    handlers: {
      "whatsapp:message": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        loadConversations();
      },
      "whatsapp:status": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        loadConversations();
      },
      "whatsapp:read": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        loadConversations();
      },
    },
  });

  const sorted = useMemo(() => {
    // Unread first, then most recent — the order reception actually works in.
    return [...conversations].sort((a, b) => {
      const unreadDiff = (b.unreadCount > 0) - (a.unreadCount > 0);
      if (unreadDiff !== 0) return unreadDiff;
      return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
    });
  }, [conversations]);

  const unreadTotal = conversations.reduce(
    (sum, c) => sum + (c.unreadCount || 0),
    0,
  );

  const openThread = (conversation) => {
    setOpenConversationId(conversation._id);
    setShowInbox(true);
  };

  return (
    <>
      <Card className="section-card mb-3 border-0 rounded-0">
        <Card.Header className="bg-white border-0 section-card__header">
          <Stack direction="horizontal" gap={2} className="align-items-start w-100">
            {practices.length > 1 && (
              <Form.Select
                size="sm"
                style={{ maxWidth: 160 }}
                value={practiceNumber}
                onChange={(e) => setPracticeNumber(e.target.value)}
                aria-label="Practice"
              >
                {practices.map((p) => (
                  <option key={p.practiceNumber} value={p.practiceNumber}>
                    {p.label}
                  </option>
                ))}
              </Form.Select>
            )}

            <div className="pe-2">
              <h6 className="text-primary mb-0 fw-semibold">WhatsApp</h6>
              <small className="text-muted">Patient messages</small>
            </div>

            <div className="ms-auto d-flex align-items-center gap-2">
              {unreadTotal > 0 && <Badge bg="danger">{unreadTotal}</Badge>}
              {connected && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setOpenConversationId(null);
                    setShowInbox(true);
                  }}
                >
                  Open inbox
                </Button>
              )}
            </div>
          </Stack>
        </Card.Header>

        <Card.Body className="section-card__body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading messages…</p>
            </div>
          ) : !connected ? (
            // An unconfigured inbox and an empty one mean very different
            // things, so they do not look the same.
            <p className="mb-0">
              WhatsApp is not connected for this practice.{" "}
              <Button
                variant="link"
                className="p-0 text-decoration-none align-baseline"
                onClick={() => navigate("/practice")}
              >
                Set it up in Practice Settings
              </Button>
              .
            </p>
          ) : !sorted.length ? (
            <p className="mb-0">No messages yet.</p>
          ) : (
            sorted
              .slice(0, PREVIEW_COUNT)
              .map((c) => (
                <ThreadRow key={c._id} conversation={c} onSelect={openThread} />
              ))
          )}
        </Card.Body>
      </Card>

      <InboxModal
        show={showInbox}
        onHide={() => setShowInbox(false)}
        practiceNumber={practiceNumber}
        practiceName={labelFor(practiceNumber)}
        conversations={sorted}
        templates={templates}
        loadingThreads={loading}
        initialConversationId={openConversationId}
        onConversationsChanged={loadConversations}
        onViewPatient={(patientId) => navigate(`/patients/info/${patientId}`)}
        showToast={showToast}
      />

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
